import React, { useState, useEffect, useCallback } from "react";
import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Button, IconButton, Dialog, DialogTitle,
  DialogContent, DialogActions, TextField, MenuItem, Select, FormControl,
  InputLabel, Switch, Chip, Pagination, Tooltip, Link
} from "@mui/material";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import ContactMailIcon from "@mui/icons-material/ContactMail";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import { fetchData, postData, putData, deleteData } from "../../BaseUrl";
import Toast from "../../DynamicComponent/Toast";

const PAGE_SIZE = 10;

const FIELD_TYPES = ["text", "email", "phone", "textarea", "select"];

const DEFAULT_FIELDS = [
  { _id: "1", fieldLabel: "Your Name", fieldType: "text", placeholder: "Enter your full name", isActive: true },
  { _id: "2", fieldLabel: "Phone No.", fieldType: "phone", placeholder: "Enter your phone number", isActive: true },
  { _id: "3", fieldLabel: "Email Address", fieldType: "email", placeholder: "Enter your email address", isActive: true },
  { _id: "4", fieldLabel: "Subject", fieldType: "text", placeholder: "Enter subject", isActive: true },
  { _id: "5", fieldLabel: "Write a Message", fieldType: "textarea", placeholder: "Write your message here...", isActive: true },
];

const emptyForm = { fieldLabel: "", fieldType: "text", placeholder: "", isActive: true };

function ConfirmDialog({ open, onClose, onConfirm }) {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontWeight: 700, fontSize: "16px" }}>Confirm Delete</DialogTitle>
      <DialogContent>
        <Typography sx={{ fontSize: "14px", color: "#374151" }}>Are you sure you want to delete this field?</Typography>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} sx={{ textTransform: "none", color: "#6B7280" }}>Cancel</Button>
        <Button onClick={onConfirm} variant="contained" color="error" sx={{ textTransform: "none", borderRadius: "8px" }}>Delete</Button>
      </DialogActions>
    </Dialog>
  );
}

const AdminContactUsConfig = () => {
  const [rows, setRows] = useState([]);
  const [page, setPage] = useState(1);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [feedbackText, setFeedbackText] = useState("Do you want to give Feedback? Give us your Feedback");
  const [editFeedback, setEditFeedback] = useState(false);
  const [toast, setToast] = useState({ open: false, severity: "success", message: "" });

  const load = useCallback(async () => {
    try {
      const res = await fetchData("admin/contact-fields/list");
      setRows(Array.isArray(res?.data) ? res.data : DEFAULT_FIELDS);
    } catch { setRows(DEFAULT_FIELDS); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const showToast = (severity, message) => setToast({ open: true, severity, message });

  const openAdd = () => { setEditId(null); setForm(emptyForm); setDialogOpen(true); };
  const openEdit = (row) => {
    setEditId(row._id || row.id);
    setForm({ fieldLabel: row.fieldLabel || "", fieldType: row.fieldType || "text", placeholder: row.placeholder || "", isActive: row.isActive !== false });
    setDialogOpen(true);
  };
  const closeDialog = () => { setDialogOpen(false); setForm(emptyForm); setEditId(null); };

  const handleSave = async () => {
    if (!form.fieldLabel.trim()) { showToast("error", "Field Label is required."); return; }
    try {
      if (editId) {
        await putData(`admin/contact-fields/update/${editId}`, form);
        showToast("success", "Field updated.");
      } else {
        await postData("admin/contact-fields/create", form);
        showToast("success", "Field added.");
      }
      closeDialog(); load();
    } catch { showToast("error", "Failed to save."); }
  };

  const handleToggleActive = async (row) => {
    const id = row._id || row.id;
    const newVal = !row.isActive;
    setRows(prev => prev.map(r => (r._id === id || r.id === id) ? { ...r, isActive: newVal } : r));
    try {
      await putData(`admin/contact-fields/update/${id}`, { ...row, isActive: newVal });
    } catch { load(); }
  };

  const handleDelete = async () => {
    const id = deleteTarget?._id || deleteTarget?.id;
    setConfirmOpen(false);
    try {
      await deleteData(`admin/contact-fields/${id}`);
      showToast("success", "Field deleted.");
      load();
    } catch { showToast("error", "Failed to delete."); }
  };

  const handleUpdate = async () => {
    showToast("success", "Contact Us configuration saved.");
  };

  const pageData = rows.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const btnStyle = {
    background: "linear-gradient(90deg, #4A7BA8, #244C77)",
    color: "#fff", fontSize: "13px", textTransform: "none",
    borderRadius: "10px", px: 2.5, py: 1,
    "&:hover": { background: "linear-gradient(90deg, #4A7BA8, #244C77)" },
  };

  return (
    <div style={{ textAlign: "left", padding: "1rem" }}>
      <Toast open={toast.open} handleClose={() => setToast({ ...toast, open: false })} severity={toast.severity} message={toast.message} />
      <ConfirmDialog open={confirmOpen} onClose={() => setConfirmOpen(false)} onConfirm={handleDelete} />

      <p style={{ color: "rgba(0,0,0,0.45)", fontWeight: "bold", fontSize: "14px", margin: "0 0 12px" }}>
        Contact Us / Contact Us displayed in the website
      </p>

      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 3 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <ContactMailIcon sx={{ color: "#244C77", fontSize: "28px" }} />
          <Box>
            <Typography sx={{ fontWeight: "bold", fontSize: "22px", color: "#111827" }}>Contact Us Configuration</Typography>
            <Typography sx={{ fontSize: "13px", color: "#6B7280" }}>Manage contact form fields displayed on the website</Typography>
          </Box>
        </Box>
        <Button startIcon={<AddIcon />} onClick={openAdd} sx={btnStyle}>+ Add Contact</Button>
      </Box>

      <Paper elevation={0} sx={{ borderRadius: "16px", boxShadow: "0 2px 12px rgba(0,0,0,0.07)", overflow: "hidden" }}>
        <TableContainer>
          <Table>
            <TableHead sx={{ backgroundColor: "#1E293B" }}>
              <TableRow>
                {["S.No", "Field Label", "Field Type", "Placeholder", "Active", "Actions"].map(h => (
                  <TableCell key={h} sx={{ color: "#fff", fontWeight: 600, fontSize: "13px" }}>{h}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {pageData.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4, color: "#9CA3AF" }}>No contact fields configured.</TableCell>
                </TableRow>
              ) : pageData.map((row, idx) => (
                <TableRow key={row._id || row.id || idx}
                  sx={{ "&:hover": { backgroundColor: "#f9fafb" }, backgroundColor: idx % 2 === 0 ? "#fff" : "#FAFAFA" }}>
                  <TableCell sx={{ fontSize: "13px", width: 60 }}>{(page - 1) * PAGE_SIZE + idx + 1}</TableCell>
                  <TableCell sx={{ fontSize: "13px", fontWeight: 600, color: "#111827" }}>{row.fieldLabel}</TableCell>
                  <TableCell>
                    <Chip label={row.fieldType} size="small"
                      sx={{ backgroundColor: "#EEF2FF", color: "#244C77", fontWeight: 600, fontSize: "11px", textTransform: "capitalize" }} />
                  </TableCell>
                  <TableCell sx={{ fontSize: "13px", color: "#6B7280", maxWidth: 200 }}>
                    <Typography noWrap sx={{ fontSize: "13px", maxWidth: 180 }}>{row.placeholder}</Typography>
                  </TableCell>
                  <TableCell>
                    <Switch checked={row.isActive !== false} onChange={() => handleToggleActive(row)} size="small"
                      sx={{ "& .MuiSwitch-switchBase.Mui-checked": { color: "#4caf50" }, "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { backgroundColor: "#4caf50" } }} />
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: "flex", gap: 0.5 }}>
                      <Tooltip title="Edit">
                        <IconButton size="small" onClick={() => openEdit(row)} sx={{ color: "#244C77", "&:hover": { backgroundColor: "#EEF2FF" } }}>
                          <EditRoundedIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton size="small" onClick={() => { setDeleteTarget(row); setConfirmOpen(true); }} sx={{ color: "#EF4444", "&:hover": { backgroundColor: "#FEF2F2" } }}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {rows.length > PAGE_SIZE && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
            <Pagination count={Math.ceil(rows.length / PAGE_SIZE)} page={page}
              onChange={(_, v) => setPage(v)} color="primary" size="small" />
          </Box>
        )}
      </Paper>

      {/* Feedback row */}
      <Paper elevation={0} sx={{ borderRadius: "16px", boxShadow: "0 2px 12px rgba(0,0,0,0.07)", mt: 2.5, p: 2.5 }}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 1 }}>
          {editFeedback ? (
            <TextField size="small" value={feedbackText}
              onChange={e => setFeedbackText(e.target.value)}
              sx={{ flex: 1, minWidth: 260, "& .MuiOutlinedInput-root": { borderRadius: "10px" } }} />
          ) : (
            <Typography sx={{ fontSize: "14px", color: "#374151" }}>
              {feedbackText.split("Give us your Feedback")[0]}
              <Link href="#" underline="hover" sx={{ color: "#244C77", fontWeight: 600 }}>
                Give us your Feedback
              </Link>
            </Typography>
          )}
          <Box sx={{ display: "flex", gap: 0.5 }}>
            <Tooltip title="Edit feedback text">
              <IconButton size="small" onClick={() => setEditFeedback(p => !p)} sx={{ color: "#244C77" }}>
                <EditRoundedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Delete feedback row">
              <IconButton size="small" sx={{ color: "#EF4444" }}>
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      </Paper>

      <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 3 }}>
        <Button startIcon={<SaveOutlinedIcon />} onClick={handleUpdate} sx={btnStyle}>Update</Button>
      </Box>

      {/* Add / Edit Dialog */}
      <Dialog open={dialogOpen} onClose={closeDialog} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, fontSize: "17px", color: "#111827" }}>
          {editId ? "Edit Contact Field" : "Add Contact Field"}
        </DialogTitle>
        <DialogContent sx={{ pt: "12px !important" }}>
          <TextField label="Field Label" fullWidth size="small" value={form.fieldLabel}
            onChange={e => setForm(p => ({ ...p, fieldLabel: e.target.value }))}
            sx={{ mb: 2, "& .MuiOutlinedInput-root": { borderRadius: "10px" } }} />
          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel>Field Type</InputLabel>
            <Select value={form.fieldType} onChange={e => setForm(p => ({ ...p, fieldType: e.target.value }))} label="Field Type"
              sx={{ borderRadius: "10px" }}>
              {FIELD_TYPES.map(t => <MenuItem key={t} value={t} sx={{ textTransform: "capitalize" }}>{t}</MenuItem>)}
            </Select>
          </FormControl>
          <TextField label="Placeholder" fullWidth size="small" value={form.placeholder}
            onChange={e => setForm(p => ({ ...p, placeholder: e.target.value }))}
            sx={{ mb: 2, "& .MuiOutlinedInput-root": { borderRadius: "10px" } }} />
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography sx={{ fontSize: "13px", color: "#374151" }}>Is Active</Typography>
            <Switch checked={form.isActive} onChange={e => setForm(p => ({ ...p, isActive: e.target.checked }))} size="small"
              sx={{ "& .MuiSwitch-switchBase.Mui-checked": { color: "#4caf50" }, "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { backgroundColor: "#4caf50" } }} />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={closeDialog} sx={{ textTransform: "none", color: "#6B7280" }}>Cancel</Button>
          <Button onClick={handleSave} variant="contained" sx={{ ...btnStyle, px: 3, py: 0.8 }}>
            {editId ? "Update" : "Add"}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default AdminContactUsConfig;
